import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import axiosConn from '../../axiosConn';
import './localBusinessSpotlight.css';

const INITIAL_FORM = {
  businessName: '',
  contactName: '',
  emailAddress: '',
  phoneNumber: '',
  businessCategory: '',
  businessWebsiteOrSocialUrl: '',
  destinationUrl: '',
  requestHelpWithDestination: false,
  creativePreference: 'create_for_me',
  adHeadline: '',
  creativeImageUrl: '',
  logoUrl: '',
  additionalAssetUrls: '',
  mainOfferOrCallToAction: '',
  brandNotes: '',
  planKey: 'six_month',
  termsAccepted: false,
  assetAuthorizationAccepted: false,
  websiteConfirmation: '',
};

const createIdempotencyKey = () => {
  const randomPart =
    window.crypto && window.crypto.getRandomValues
      ? Array.from(window.crypto.getRandomValues(new Uint32Array(4)))
          .map((value) => value.toString(16))
          .join('')
      : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  return `spotlight-${Date.now()}-${randomPart}`;
};

const getApiMessage = (error) =>
  error?.response?.data?.message ||
  'Checkout could not be started. Please try again or contact support@joegalaxy.net.';

const formatPlanPrice = (plan) => {
  const amount = Number(plan?.amountCents || 0) / 100;
  if (plan?.planKey === 'monthly') return `$${amount}/month`;
  if (plan?.planKey === 'six_month') return `$${amount} every six months`;
  return `$${amount}/year`;
};

const planDetail = (planKey) => {
  if (planKey === 'monthly') return 'Flexible start';
  if (planKey === 'six_month') return 'Best value · $25/month equivalent';
  return 'Founding rate · $25/month equivalent';
};

const planBadge = (planKey) => {
  if (planKey === 'six_month') return 'Best value';
  if (planKey === 'annual') return 'Founding rate';
  return 'Flexible';
};

const isPreviewImageUrl = (value) =>
  /^https?:\/\/.+\.(avif|gif|jpe?g|png|webp)(\?.*)?$/i.test(
    String(value || '').trim(),
  );

export default function LocalBusinessSpotlightLanding() {
  const attribution = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      salespersonCode: params.get('rep') || '',
      source: params.get('source') || '',
      utmSource: params.get('utm_source') || '',
      utmCampaign: params.get('utm_campaign') || '',
    };
  }, []);
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState('');
  const [form, setForm] = useState(INITIAL_FORM);
  const [idempotencyKey] = useState(createIdempotencyKey);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    let active = true;
    axiosConn
      .get('local-business-spotlight/public/config')
      .then((response) => {
        if (active) setConfig(response.data?.data || null);
      })
      .catch(() => {
        if (active) {
          setConfigError(
            'Spotlight enrollment is temporarily unavailable. Please contact support@joegalaxy.net.',
          );
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.add('spotlight-page-active');
    document.body.classList.add('spotlight-page-active');
    return () => {
      document.documentElement.classList.remove('spotlight-page-active');
      document.body.classList.remove('spotlight-page-active');
    };
  }, []);

  const updateField = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const checkoutUnavailable =
    !config || !config.enabled || !config.checkoutAvailable || config.soldOut;
  const selectedPlan = (config?.plans || []).find(
    (plan) => plan.planKey === form.planKey,
  );
  const previewBusinessName = form.businessName.trim() || 'Your Business Name';
  const previewCategory = form.businessCategory.trim() || 'Camden local business';
  const previewOffer =
    form.mainOfferOrCallToAction.trim() ||
    'Feature your main offer, service, or reason to visit.';
  const previewDestination =
    form.businessWebsiteOrSocialUrl.trim() ||
    form.destinationUrl.trim() ||
    'Website, Facebook, or phone';
  const previewCreativeUrl =
    form.creativePreference === 'ready_made' &&
    isPreviewImageUrl(form.creativeImageUrl)
      ? form.creativeImageUrl.trim()
      : '';
  const previewCta = form.destinationUrl.trim()
    ? 'Open offer'
    : form.requestHelpWithDestination
    ? 'Destination help requested'
    : 'Visit business';

  const submit = async (event) => {
    event.preventDefault();
    if (checkoutUnavailable || submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const payload = {
        ...form,
        ...attribution,
        additionalAssetUrls: form.additionalAssetUrls
          .split(/[\n,]/)
          .map((value) => value.trim())
          .filter(Boolean),
      };
      const response = await axiosConn.post(
        'local-business-spotlight/public/checkout',
        payload,
        { headers: { 'Idempotency-Key': idempotencyKey } },
      );
      const checkoutUrl = response.data?.data?.checkoutUrl;
      if (!checkoutUrl) throw new Error('Checkout URL missing');
      window.location.assign(checkoutUrl);
    } catch (error) {
      setSubmitError(getApiMessage(error));
      setSubmitting(false);
    }
  };

  return (
    <main className="spotlight-page">
      <Helmet>
        <title>JG Local Business Spotlight | Camden</title>
        <meta
          name="description"
          content="Put your clickable business ad in front of a growing network of Camden-area local players."
        />
      </Helmet>

      <section className="spotlight-hero">
        <div className="spotlight-shell spotlight-hero-grid">
          <div>
            <p className="spotlight-eyebrow">
              JG LOCAL BUSINESS SPOTLIGHT &mdash; FOUNDING 30
            </p>
            <h1>STAY VISIBLE WHERE CAMDEN GETS THINGS DONE.</h1>
            <p className="spotlight-lede">
              Put your clickable business ad in front of a growing network of
              Camden-area local players.
            </p>
            <div className="spotlight-scarcity">
              <strong>Only 30 active founding business spots.</strong>
              <span>
                A capped rotation means less ad crowding and stronger founding
                share of voice.
              </span>
            </div>
            <a className="spotlight-primary-link" href="#claim-your-spot">
              CLAIM YOUR FOUNDING SPOT
            </a>
          </div>
          <aside className="spotlight-hero-preview" aria-label="Spotlight ad preview">
            <span>Camden Spotlight</span>
            <div className="spotlight-preview-card">
              <div className="spotlight-preview-orbit" aria-hidden="true" />
              <p>Approved rotation</p>
              <h2>Clickable local business spotlight</h2>
              <small>
                Preview your ad before checkout. We review every placement before
                it goes live.
              </small>
              <button type="button">Visit business</button>
            </div>
          </aside>
        </div>
      </section>

      <section className="spotlight-shell spotlight-benefits" aria-label="Benefits">
        <article>
          <span>LOCAL</span>
          <h2>Reach Camden-area local players.</h2>
          <p>
            Be present where people are finding help, earning, connecting and
            supporting nearby businesses.
          </p>
        </article>
        <article>
          <span>REPEATED</span>
          <h2>Stay in approved rotation.</h2>
          <p>
            Your business rotates across approved JG LocalOps and JoeGalaxy
            surfaces while your subscription remains eligible.
          </p>
        </article>
        <article>
          <span>TRACKABLE</span>
          <h2>Know when activity begins.</h2>
          <p>
            Recorded ad displays and clicks begin when your advertisement goes
            live. You receive an email when someone clicks through.
          </p>
        </article>
        <article>
          <span>DONE FOR YOU</span>
          <h2>Bring an ad&mdash;or let us help.</h2>
          <p>
            Already have an ad? Submit it. Need one? JG LocalOps can create it
            from materials you authorize us to use.
          </p>
        </article>
      </section>

      <section className="spotlight-shell spotlight-how">
        <div>
          <p className="spotlight-eyebrow">HOW IT WORKS</p>
          <h2>From local business to live placement.</h2>
        </div>
        <ol>
          <li>Choose your plan.</li>
          <li>Tell us about your business and offer.</li>
          <li>Submit an existing ad or ask us to create one.</li>
          <li>Complete secure Stripe checkout.</li>
          <li>We review and prepare the placement.</li>
          <li>We email you when the ad is live.</li>
          <li>Recorded displays, clicks and click notifications begin at launch.</li>
        </ol>
      </section>

      <section
        className="spotlight-shell spotlight-enrollment"
        id="claim-your-spot"
      >
        <header>
          <p className="spotlight-eyebrow">CLAIM YOUR FOUNDING SPOT</p>
          <h2>Choose your plan. Submit your business. Get seen locally.</h2>
        </header>

        {configError ? (
          <div className="spotlight-notice spotlight-notice--error">
            {configError}
          </div>
        ) : null}
        {config && checkoutUnavailable ? (
          <div className="spotlight-notice" role="status">
            {config.soldOut
              ? 'The Founding 30 is currently full.'
              : config.unavailableReason ||
                'Spotlight checkout is temporarily unavailable.'}
          </div>
        ) : null}

        <form className="spotlight-form" onSubmit={submit}>
          <div className="spotlight-enrollment-layout">
            <div className="spotlight-form-main">
          <fieldset className="spotlight-form-section" disabled={!config}>
            <legend>
              <span>1</span>
              Choose your plan
            </legend>
            <div className="spotlight-plan-grid">
            {(config?.plans || []).map((plan) => (
              <label
                className={
                  form.planKey === plan.planKey
                    ? 'spotlight-plan spotlight-plan--selected'
                    : 'spotlight-plan'
                }
                key={plan.planKey}
              >
                <input
                  type="radio"
                  name="planKey"
                  value={plan.planKey}
                  checked={form.planKey === plan.planKey}
                  onChange={updateField}
                />
                <span className="spotlight-control-mark" aria-hidden="true" />
                <span className="spotlight-plan-badge">{planBadge(plan.planKey)}</span>
                <strong>{plan.displayName}</strong>
                <span>{formatPlanPrice(plan)}</span>
                <small>{planDetail(plan.planKey)}</small>
              </label>
            ))}
            </div>
          </fieldset>

          <section className="spotlight-form-section">
            <h3><span>2</span> Business basics</h3>
          <div className="spotlight-field-grid">
            <label>
              Business name
              <input name="businessName" value={form.businessName} onChange={updateField} required />
            </label>
            <label>
              Contact name
              <input name="contactName" value={form.contactName} onChange={updateField} required />
            </label>
            <label>
              Email
              <input type="email" name="emailAddress" value={form.emailAddress} onChange={updateField} required />
            </label>
            <label>
              Phone <small>(optional)</small>
              <input name="phoneNumber" value={form.phoneNumber} onChange={updateField} />
            </label>
            <label>
              Business category
              <input name="businessCategory" value={form.businessCategory} onChange={updateField} required />
            </label>
            <label>
              Website or business social page
              <input type="url" name="businessWebsiteOrSocialUrl" value={form.businessWebsiteOrSocialUrl} onChange={updateField} placeholder="https://" required />
            </label>
          </div>
          </section>

          <fieldset className="spotlight-form-section spotlight-choice">
            <legend>
              <span>3</span>
              Creative direction
            </legend>
            <label>
              <input type="radio" name="creativePreference" value="create_for_me" checked={form.creativePreference === 'create_for_me'} onChange={updateField} />
              <span className="spotlight-control-mark" aria-hidden="true" />
              <span>
                <strong>Create it for me</strong>
                <small>Use approved business materials and notes.</small>
              </span>
            </label>
            <label>
              <input type="radio" name="creativePreference" value="ready_made" checked={form.creativePreference === 'ready_made'} onChange={updateField} />
              <span className="spotlight-control-mark" aria-hidden="true" />
              <span>
                <strong>I have a ready-made ad</strong>
                <small>Submit your prepared creative and headline.</small>
              </span>
            </label>
          </fieldset>

          <section className="spotlight-form-section">
            <h3><span>4</span> Links and destination</h3>
          <div className="spotlight-field-grid">
            {form.creativePreference === 'ready_made' ? (
              <>
                <label>
                  Ad headline
                  <input name="adHeadline" value={form.adHeadline} onChange={updateField} required />
                </label>
                <label>
                  Creative image or video URL
                  <input type="url" name="creativeImageUrl" value={form.creativeImageUrl} onChange={updateField} placeholder="https://" required />
                </label>
              </>
            ) : (
              <>
                <label>
                  Logo or primary photo URL <small>(optional)</small>
                  <input type="url" name="logoUrl" value={form.logoUrl} onChange={updateField} placeholder="https://" />
                </label>
                <label>
                  Additional authorized asset URLs <small>(optional, up to five)</small>
                  <textarea name="additionalAssetUrls" value={form.additionalAssetUrls} onChange={updateField} placeholder="One HTTPS URL per line" />
                </label>
              </>
            )}
            <label className="spotlight-field-wide">
              Main offer and desired call to action
              <textarea name="mainOfferOrCallToAction" value={form.mainOfferOrCallToAction} onChange={updateField} required />
            </label>
            <label className="spotlight-field-wide">
              Brand or wording notes <small>(optional)</small>
              <textarea name="brandNotes" value={form.brandNotes} onChange={updateField} />
            </label>
            <label>
              Click destination URL
              <input type="url" name="destinationUrl" value={form.destinationUrl} onChange={updateField} placeholder="https://" disabled={form.requestHelpWithDestination} required={!form.requestHelpWithDestination} />
            </label>
            <label className="spotlight-checkbox">
              <input type="checkbox" name="requestHelpWithDestination" checked={form.requestHelpWithDestination} onChange={updateField} />
              <span className="spotlight-control-mark" aria-hidden="true" />
              <span>I need help choosing the click destination.</span>
            </label>
          </div>
          </section>

          <label className="spotlight-honeypot" aria-hidden="true">
            Website confirmation
            <input name="websiteConfirmation" value={form.websiteConfirmation} onChange={updateField} tabIndex="-1" autoComplete="off" />
          </label>

          <section className="spotlight-form-section spotlight-checkout-module">
            <h3><span>5</span> Review and checkout</h3>
          <div className="spotlight-checkout-card">
            <div>
              <p>Selected plan</p>
              <strong>
                {selectedPlan
                  ? `${selectedPlan.displayName} · ${formatPlanPrice(selectedPlan)}`
                  : 'Choose a plan'}
              </strong>
            </div>
            <span>Secure Stripe checkout</span>
          </div>
          <div className="spotlight-consents">
            <label>
              <input type="checkbox" name="assetAuthorizationAccepted" checked={form.assetAuthorizationAccepted} onChange={updateField} required />
              <span className="spotlight-control-mark" aria-hidden="true" />
              <span>
              I confirm that I own or have permission to use the materials I
              submitted, and I authorize JG LocalOps to use those materials&mdash;or
              publicly available materials from the business pages I
              provided&mdash;to create and display my advertisement.
              </span>
            </label>
            <label>
              <input type="checkbox" name="termsAccepted" checked={form.termsAccepted} onChange={updateField} required />
              <span className="spotlight-control-mark" aria-hidden="true" />
              <span>
              I understand that advertisements are subject to review, active
              billing and placement availability. Clicks, leads and sales are
              not guaranteed.
              </span>
            </label>
          </div>

          {submitError ? (
            <div className="spotlight-notice spotlight-notice--error" role="alert">
              {submitError}
            </div>
          ) : null}
          <button
            className="spotlight-submit"
            type="submit"
            disabled={checkoutUnavailable || submitting}
          >
            {submitting
              ? 'Opening secure checkout...'
              : config?.soldOut
              ? 'FOUNDING 30 FULL'
              : 'CONTINUE TO SECURE CHECKOUT'}
          </button>
          <div className="spotlight-trust-strip" aria-label="Checkout confidence">
            <span>Secure Stripe checkout</span>
            <span>Reviewed before going live</span>
            <span>Eligible while billing and placement remain active</span>
            <span>Results are not guaranteed</span>
          </div>
          <p className="spotlight-disclosure">
            Advertising results are not guaranteed. Ads are subject to review,
            active billing and placement availability. Founding plans include
            one reasonable creative or offer update per month. Need help?
            Email support@joegalaxy.net.
          </p>
          </section>
            </div>
            <aside
              className="spotlight-summary"
              aria-label="Live Spotlight submission preview"
            >
              <div className="spotlight-live-preview">
                <p className="spotlight-eyebrow">YOUR SPOTLIGHT PREVIEW</p>
                <div className="spotlight-preview-card spotlight-preview-card--live">
                  <div className="spotlight-preview-orbit" aria-hidden="true" />
                  <span>Camden Spotlight</span>
                  {previewCreativeUrl ? (
                    <img
                      className="spotlight-preview-media"
                      src={previewCreativeUrl}
                      alt=""
                    />
                  ) : null}
                  <h3>{previewBusinessName}</h3>
                  <p>{previewCategory}</p>
                  <strong>{previewOffer}</strong>
                  <small>{previewDestination}</small>
                  <button type="button">{previewCta}</button>
                </div>
              </div>
              <div className="spotlight-mini-summary">
                <p>Selected plan</p>
                <strong>
                  {selectedPlan
                    ? `${selectedPlan.displayName} · ${formatPlanPrice(selectedPlan)}`
                    : 'Choose a plan'}
                </strong>
                <span>Founding 30 capped rotation</span>
                <span>Secure checkout</span>
                <span>Reviewed before live</span>
                <span>Support: support@joegalaxy.net</span>
              </div>
              <div className="spotlight-next-steps" aria-label="What happens next">
                <p>What happens next</p>
                <ol>
                  <li>Complete secure Stripe checkout.</li>
                  <li>We review your ad details before launch.</li>
                  <li>Your approved Spotlight enters the capped Camden rotation.</li>
                </ol>
              </div>
            </aside>
          </div>
        </form>
      </section>
    </main>
  );
}
